import { calculateChart } from "celestine";

/** Fixed house system for all charts. */
const HOUSE_SYSTEM = "placidus";

/**
 * @typedef {object} BirthInput
 * @property {number} year
 * @property {number} month
 * @property {number} day
 * @property {number} hour
 * @property {number} minute
 * @property {number} [second]
 * @property {number} timezone - offset hours from UTC (e.g. -4 for EDT)
 * @property {number} latitude
 * @property {number} longitude
 */

/**
 * @typedef {object} BodyRow
 * @property {string} name
 * @property {number} longitude
 * @property {string} signName
 * @property {number} degree
 * @property {number} minute
 * @property {string} formatted
 * @property {number|null} house
 * @property {boolean} isRetrograde
 * @property {'planet'|'node'|'angle'} kind
 */

/**
 * @typedef {object} AspectRow
 * @property {string} body1
 * @property {string} body2
 * @property {string} type
 * @property {number} orb
 * @property {string} [symbol]
 */

/**
 * @typedef {object} ChartModel
 * @property {object} input
 * @property {string} houseSystem
 * @property {string} houseSystemName
 * @property {BodyRow[]} bodies
 * @property {BodyRow[]} angles
 * @property {{ house: number, longitude: number, signName: string, formatted: string }[]} cusps
 * @property {AspectRow[]} aspects
 * @property {object} raw - full Celestine chart (for later interpretation pass)
 */

const MAJOR_PLANET_ORDER = [
  "Sun",
  "Moon",
  "Mercury",
  "Venus",
  "Mars",
  "Jupiter",
  "Saturn",
  "Uranus",
  "Neptune",
  "Pluto",
  "Chiron",
];

/**
 * @param {BirthInput} birth
 * @returns {ChartModel}
 */
export function computeChartModel(birth) {
  const houseSystem = HOUSE_SYSTEM;

  const chart = calculateChart(
    {
      year: birth.year,
      month: birth.month,
      day: birth.day,
      hour: birth.hour,
      minute: birth.minute,
      second: birth.second ?? 0,
      timezone: birth.timezone,
      latitude: birth.latitude,
      longitude: birth.longitude,
    },
    {
      houseSystem,
      includeChiron: true,
      includeAsteroids: false,
      includeLots: false,
      includeLilith: false,
      includeNodes: "true",
      includePatterns: true,
      // Orb thresholds per major aspect (deg from exact). Conjunction stays at
      // the library default 8°, but opposition/square/trine/sextile are tightened
      // to match the readings Spec §8.
      aspectOrbs: {
        conjunction: 8,
        opposition: 7,
        square: 6,
        trine: 6,
        sextile: 4,
      },
    }
  );

  /** @type {BodyRow[]} */
  const bodies = [];

  const planetByName = new Map(chart.planets.map((p) => [p.name, p]));
  for (const name of MAJOR_PLANET_ORDER) {
    const p = planetByName.get(name);
    if (!p) continue;
    bodies.push({
      name: p.name,
      longitude: p.longitude,
      signName: p.signName,
      degree: p.degree,
      minute: p.minute,
      formatted: p.formatted,
      house: p.house,
      isRetrograde: Boolean(p.isRetrograde),
      kind: "planet",
    });
  }

  for (const node of chart.nodes || []) {
    bodies.push({
      name: node.name,
      longitude: node.longitude,
      signName: node.signName,
      degree: node.degree,
      minute: node.minute,
      formatted: node.formatted,
      house: node.house ?? null,
      isRetrograde: false,
      kind: "node",
    });
  }

  const angleDefs = [
    ["Ascendant", chart.angles.ascendant],
    ["Descendant", chart.angles.descendant],
    ["Midheaven", chart.angles.midheaven],
    ["Imum Coeli", chart.angles.imumCoeli],
  ];

  /** @type {BodyRow[]} */
  const angles = angleDefs.map(([name, a]) => ({
    name,
    longitude: a.longitude,
    signName: a.signName,
    degree: a.degree,
    minute: a.minute,
    formatted: a.formatted,
    house: null,
    isRetrograde: false,
    kind: "angle",
  }));

  const cusps = (chart.houses.cusps || []).map((c) => ({
    house: c.house,
    longitude: c.longitude,
    signName: c.signName,
    formatted: c.formatted,
  }));

  const aspects = [
    ...(chart.aspects?.all || []).map((a) => ({
      body1: a.body1,
      body2: a.body2,
      type: formatAspectType(a.type),
      // Celestine: deviation = how far from exact; orb = allowed max
      orb:
        typeof a.deviation === "number"
          ? a.deviation
          : typeof a.orb === "number"
            ? a.orb
            : Number(a.orb) || 0,
      symbol: a.symbol || "",
    })),
    // Minor friction aspects (semi-square 45°, sesquisquare 135°). Celestine's
    // chart detection only emits the five majors, so we derive these here from
    // the same point set (planets, Chiron, nodes) and merge them in.
    ...buildMinorFrictionAspects(bodies),
  ];

  aspects.sort((x, y) => x.orb - y.orb);

  return {
    input: { ...chart.input },
    houseSystem: chart.houses.system,
    houseSystemName: chart.houses.systemName || chart.houses.system,
    bodies,
    angles,
    cusps,
    aspects,
    raw: chart,
  };
}

/** @param {unknown} type */
function formatAspectType(type) {
  if (type == null) return "";
  const s = String(type);
  // Enum may stringify as "Trine" or "AspectType.Trine"
  const bare = s.includes(".") ? s.split(".").pop() : s;
  return bare.charAt(0).toUpperCase() + bare.slice(1);
}

/**
 * Minor friction aspects: the semi-square (45°) and sesquisquare (135°).
 * The 45° is an early internal prompt that something must shift; the 135° is
 * the boundary friction that arrives when that prompt is ignored.
 */
const MINOR_FRICTION_ASPECTS = [
  { type: "Semi-square", angle: 45, orb: 2 },
  { type: "Sesquisquare", angle: 135, orb: 2 },
];

/**
 * Derive semi-square / sesquisquare aspects across the given points (planets,
 * Chiron, and nodes), matching Celestine's symmetric orb style.
 * @param {BodyRow[]} points
 * @returns {AspectRow[]}
 */
function buildMinorFrictionAspects(points) {
  /** @type {AspectRow[]} */
  const out = [];
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      const a = points[i];
      const b = points[j];
      const raw = Math.abs(a.longitude - b.longitude) % 360;
      const separation = raw > 180 ? 360 - raw : raw;
      for (const def of MINOR_FRICTION_ASPECTS) {
        const deviation = Math.abs(separation - def.angle);
        if (deviation <= def.orb) {
          out.push({
            body1: a.name,
            body2: b.name,
            type: def.type,
            orb: deviation,
            symbol: "",
          });
        }
      }
    }
  }
  return out;
}
